import * as React from 'react'
import { NavItem } from './types'
import { SIDEBAR_COLLAPSED_STORAGE_KEY } from './consts'
import { isSalesItem, isSubItemActive } from './utils'

export const useExpandedItems = (navItems: NavItem[], pathname: string) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<number>>(() => {
    const initialSet = new Set<number>()
    navItems.forEach((link, idx) => {
      const hasSubItems = link.subItems && link.subItems.length > 0
      if (hasSubItems && isSalesItem(link.label || '')) {
        initialSet.add(idx)
      }
    })
    return initialSet
  })

  // Derive from pathname + href structure only — navItems is a new array every
  // render (icons/labels), so depending on it causes an infinite setState loop.
  const expandKey = navItems
    .map((link, idx) => {
      const hasSubItems = link.subItems && link.subItems.length > 0
      if (!hasSubItems) return ''
      const open =
        isSalesItem(link.label || '') ||
        isSubItemActive(link.subItems!, pathname) ||
        (link.expandWhenPathPrefix != null &&
          pathname.startsWith(link.expandWhenPathPrefix))
      return open ? String(idx) : ''
    })
    .filter(Boolean)
    .join(',')

  React.useEffect(() => {
    const next = new Set(
      expandKey
        .split(',')
        .filter(Boolean)
        .map((s) => Number(s))
    )
    setExpandedItems((prev) => {
      if (prev.size === next.size && [...next].every((i) => prev.has(i))) {
        return prev
      }
      return next
    })
  }, [expandKey])

  const toggleExpanded = (idx: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      return next
    })
  }

  return { expandedItems, toggleExpanded }
}

export const useSidebarCollapse = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  const setCollapsed = React.useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed)
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed))
    } catch {
      // ignore storage errors
    }
  }, [])

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(!isCollapsed)
  }, [isCollapsed, setCollapsed])

  const expand = React.useCallback(() => {
    setCollapsed(false)
  }, [setCollapsed])

  return { isCollapsed, toggleCollapsed, expand }
}
